/**
 * Encryption Service
 * AES-256-GCM encryption for Premium user profile data stored in Google Drive
 *
 * Security Model:
 * - Uses Web Crypto API (SubtleCrypto) for encryption
 * - Key derived from user's Google ID + app secret using PBKDF2
 * - AES-256-GCM provides authenticated encryption
 * - Random IV per encryption operation
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recommended for GCM
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;

interface EncryptedPayload {
  version: number;
  algorithm: string;
  iv: string; // Base64 encoded
  salt: string; // Base64 encoded
  data: string; // Base64 encoded ciphertext
  authTag: string; // Included in GCM ciphertext
}

interface EncryptionResult {
  success: boolean;
  payload?: EncryptedPayload;
  error?: string;
}

interface DecryptionResult {
  success: boolean;
  data?: string;
  error?: string;
}

class EncryptionService {
  private textEncoder = new TextEncoder();
  private textDecoder = new TextDecoder();

  /**
   * Check if Web Crypto API is available
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' &&
           window.crypto &&
           typeof window.crypto.subtle !== 'undefined';
  }

  /**
   * Derive encryption key from user ID and app secret
   * Uses PBKDF2 with SHA-256
   */
  async deriveKey(userId: string, appSecret: string, salt: Uint8Array): Promise<CryptoKey> {
    if (!this.isSupported()) {
      throw new Error('Web Crypto API not supported');
    }

    // Combine userId and appSecret for key material
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      this.textEncoder.encode(`${userId}:${appSecret}`),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    // Derive AES key using PBKDF2
    const key = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt.buffer as ArrayBuffer,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: ALGORITHM, length: KEY_LENGTH },
      false, // Not extractable
      ['encrypt', 'decrypt']
    );

    return key;
  }

  /**
   * Generate random bytes for IV or salt
   */
  generateRandomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    return bytes;
  }

  /**
   * Convert Uint8Array to Base64 string
   */
  arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert Base64 string to Uint8Array
   */
  base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Encrypt data using AES-256-GCM
   *
   * @param plaintext - Data to encrypt (will be JSON stringified if object)
   * @param userId - User's Google ID for key derivation
   * @param appSecret - App secret for key derivation
   * @returns Encrypted payload with metadata
   */
  async encrypt(
    plaintext: string | object,
    userId: string,
    appSecret: string
  ): Promise<EncryptionResult> {
    try {
      if (!this.isSupported()) {
        return { success: false, error: 'Web Crypto API not supported' };
      }

      // Convert object to string if needed
      const data = typeof plaintext === 'string' ? plaintext : JSON.stringify(plaintext);

      // Generate random salt and IV
      const salt = this.generateRandomBytes(SALT_LENGTH);
      const iv = this.generateRandomBytes(IV_LENGTH);

      // Derive encryption key
      const key = await this.deriveKey(userId, appSecret, salt);

      // Encrypt data
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: ALGORITHM,
          iv: iv.buffer as ArrayBuffer,
        },
        key,
        this.textEncoder.encode(data)
      );

      // Build payload
      const payload: EncryptedPayload = {
        version: 1,
        algorithm: `${ALGORITHM}-${KEY_LENGTH}`,
        iv: this.arrayBufferToBase64(iv),
        salt: this.arrayBufferToBase64(salt),
        data: this.arrayBufferToBase64(encryptedBuffer),
        authTag: 'included', // GCM includes auth tag in ciphertext
      };

      return { success: true, payload };
    } catch (error) {
      console.error('Encryption failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Encryption failed',
      };
    }
  }

  /**
   * Decrypt data using AES-256-GCM
   *
   * @param payload - Encrypted payload from encrypt()
   * @param userId - User's Google ID for key derivation
   * @param appSecret - App secret for key derivation
   * @returns Decrypted data as string
   */
  async decrypt(
    payload: EncryptedPayload,
    userId: string,
    appSecret: string
  ): Promise<DecryptionResult> {
    try {
      if (!this.isSupported()) {
        return { success: false, error: 'Web Crypto API not supported' };
      }

      // Validate payload version
      if (payload.version !== 1) {
        return { success: false, error: `Unsupported payload version: ${payload.version}` };
      }

      // Decode Base64 values
      const salt = this.base64ToArrayBuffer(payload.salt);
      const iv = this.base64ToArrayBuffer(payload.iv);
      const ciphertext = this.base64ToArrayBuffer(payload.data);

      // Derive decryption key
      const key = await this.deriveKey(userId, appSecret, salt);

      // Decrypt data
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: ALGORITHM,
          iv: iv.buffer as ArrayBuffer,
        },
        key,
        ciphertext.buffer as ArrayBuffer
      );

      // Convert to string
      const decryptedData = this.textDecoder.decode(decryptedBuffer);

      return { success: true, data: decryptedData };
    } catch (error) {
      console.error('Decryption failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Decryption failed',
      };
    }
  }

  /**
   * Decrypt and parse JSON data
   */
  async decryptJSON<T>(
    payload: EncryptedPayload,
    userId: string,
    appSecret: string
  ): Promise<{ success: boolean; data?: T; error?: string }> {
    const result = await this.decrypt(payload, userId, appSecret);

    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }

    try {
      const parsed = JSON.parse(result.data) as T;
      return { success: true, data: parsed };
    } catch (error) {
      return { success: false, error: 'Failed to parse decrypted JSON' };
    }
  }

  /**
   * Generate a SHA-256 checksum of data
   * Used for integrity verification
   */
  async generateChecksum(data: string): Promise<string> {
    const buffer = this.textEncoder.encode(data);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
    return this.arrayBufferToBase64(hashBuffer);
  }

  /**
   * Verify data integrity using checksum
   */
  async verifyChecksum(data: string, expectedChecksum: string): Promise<boolean> {
    const actualChecksum = await this.generateChecksum(data);
    return actualChecksum === expectedChecksum;
  }
}

// Export singleton instance
export const encryptionService = new EncryptionService();

// Export types
export type { EncryptedPayload, EncryptionResult, DecryptionResult };
